import { FC } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Flex, Text, Select, TextField, IconButton } from "@radix-ui/themes";

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const CustomPagination: FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <Flex
      wrap="wrap"
      align="center"
      justify="between"
      gap="4"
      style={{ color: "var(--gray-12)" }}
    >
      <Flex align="center" gap="2">
        <Text size="2">Items per page:</Text>
        <Select.Root
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(Number(value))}
          size="1"
        >
          <Select.Trigger />
          <Select.Content>
            {pageSizeOptions.map((size) => (
              <Select.Item key={size} value={size.toString()}>
                {size}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        <Text size="2">
          {startItem.toLocaleString()} - {endItem.toLocaleString()} of{" "}
          {totalItems.toLocaleString()} items
        </Text>
      </Flex>

      <Flex align="center" gap="1">
        <IconButton
          variant="ghost"
          size="1"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title="First Page"
        >
          <ChevronsLeft size={16} />
        </IconButton>

        <IconButton
          variant="ghost"
          size="1"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title="Previous Page"
        >
          <ChevronLeft size={16} />
        </IconButton>

        <Flex align="center" gap="2" mx="2">
          <Text size="2">Page</Text>
          <TextField.Root
            size="1"
            style={{ width: "64px", textAlign: "center" }}
            type="number"
            value={currentPage.toString()}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = parseInt(e.target.value);
              if (value > 0 && value <= totalPages) {
                onPageChange(value);
              }
            }}
          />
          <Text size="2">of {totalPages.toLocaleString()}</Text>
        </Flex>

        <IconButton
          variant="ghost"
          size="1"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Next Page"
        >
          <ChevronRight size={16} />
        </IconButton>

        <IconButton
          variant="ghost"
          size="1"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          title="Last Page"
        >
          <ChevronsRight size={16} />
        </IconButton>
      </Flex>
    </Flex>
  );
};

export default CustomPagination;
